import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalAlert } from '@/hooks/useSweetAlert';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import ProcessorConfigModal from './components/ProcessorConfigModal';
import ProcessorDetailModal from './components/ProcessorDetailModal';
import ProcessorCommissionModal from './components/ProcessorCommissionModal';
import ProcessorUsageModal from './components/ProcessorUsageModal';
import CreateProcessorModal from './components/CreateProcessorModal';
import AnnualLicenseConfigCard from './components/AnnualLicenseConfigCard';
import AnnualLicenseEditorModal from './components/AnnualLicenseEditorModal';
import {
  paymentProcessors as mockProcessors,
  defaultAnnualLicenseConfig,
  type PaymentProcessor,
  type PaymentProcessorUsageFunction,
  type AnnualLicenseConfig,
} from '@/mocks/superAdminPaymentProcessors';

type LoadingState = 'loading' | 'ready' | 'error';

function simulateLoad(): Promise<PaymentProcessor[]> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.96) reject(new Error('Timeout al conectar con el servidor.'));
      else resolve([...mockProcessors].sort((a, b) => a.order - b.order));
    }, 700 + Math.random() * 300);
  });
}

export default function SuperAdminPaymentProcessors() {
  const [processors, setProcessors] = useState<PaymentProcessor[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Inline messages per processor
  const [inlineMessages, setInlineMessages] = useState<Record<string, { text: string; type: 'success' | 'warning' } | null>>({});

  // Modal states
  const [detailProc, setDetailProc] = useState<PaymentProcessor | null>(null);
  const [configProc, setConfigProc] = useState<PaymentProcessor | null>(null);
  const [commissionProc, setCommissionProc] = useState<PaymentProcessor | null>(null);
  const [usageProc, setUsageProc] = useState<PaymentProcessor | null>(null);
  const [licenseConfig, setLicenseConfig] = useState<AnnualLicenseConfig>(defaultAnnualLicenseConfig);
  const [licenseEditorOpen, setLicenseEditorOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const showInline = useCallback((procId: string, text: string, type: 'success' | 'warning') => {
    setInlineMessages((prev) => ({ ...prev, [procId]: { text, type } }));
    setTimeout(() => setInlineMessages((prev) => ({ ...prev, [procId]: null })), 3000);
  }, []);

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad()
      .then((d) => { setProcessors(d); setLoadingState('ready'); })
      .catch((err) => { setErrorMsg(err.message || 'Error desconocido'); setLoadingState('error'); });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // --- Toggle enabled ---
  const handleToggleEnabled = (proc: PaymentProcessor) => {
    const action = proc.enabled ? 'desactivar' : 'activar';
    SwalAlert.confirm(
      proc.enabled ? `¿Desactivar ${proc.name}?` : `¿Activar ${proc.name}?`,
      proc.enabled
        ? `Dejarás de procesar pagos vía ${proc.name}. Los cobros activos no se verán afectados.`
        : `${proc.name} quedará disponible como método de pago en el sistema.`,
    ).then((r) => {
      if (r.isConfirmed) {
        setProcessors((prev) => prev.map((p) => (p.id === proc.id ? { ...p, enabled: !p.enabled, status: !p.enabled ? 'active' as const : 'inactive' as const } : p)));
        showInline(proc.id, `${proc.name} ${proc.enabled ? 'desactivado' : 'activado'} correctamente`, proc.enabled ? 'warning' : 'success');
      }
    });
  };

  // --- Toggle default ---
  const handleSetDefault = (proc: PaymentProcessor) => {
    if (proc.isDefault) {
      SwalAlert.warning('Ya es predeterminado', `${proc.name} ya es el procesador predeterminado.`);
      return;
    }
    SwalAlert.confirm(
      `¿Marcar ${proc.name} como predeterminado?`,
      `${proc.name} será el procesador de pago por defecto en todo el sistema.`,
    ).then((r) => {
      if (r.isConfirmed) {
        setProcessors((prev) =>
          prev.map((p) => ({ ...p, isDefault: p.id === proc.id })),
        );
        showInline(proc.id, `${proc.name} ahora es el predeterminado`, 'success');
      }
    });
  };

  // --- Test connection ---
  const handleTestConnection = (proc: PaymentProcessor) => {
    if (proc.category === 'manual') {
      SwalAlert.warning('Método manual', 'Los métodos manuales no requieren prueba de conexión.');
      return;
    }
    if (!proc.enabled) {
      SwalAlert.warning('Procesador inactivo', `Activá ${proc.name} antes de probar la conexión.`);
      return;
    }

    SwalAlert.loading('Probando conexión...', `Verificando endpoint de ${proc.name}...`);

    setTimeout(() => {
      const success = Math.random() > 0.2;
      Swal.close();

      if (success) {
        setProcessors((prev) =>
          prev.map((p) =>
            p.id === proc.id
              ? { ...p, webhookStatus: 'healthy' as const, lastTest: new Date().toISOString().slice(0, 16).replace('T', ' ') }
              : p,
          ),
        );
        SwalAlert.success('Conexión exitosa', `${proc.name} respondió correctamente. Latencia: ${Math.floor(80 + Math.random() * 200)}ms.`);
      } else {
        setProcessors((prev) =>
          prev.map((p) =>
            p.id === proc.id
              ? { ...p, webhookStatus: 'failing' as const }
              : p,
          ),
        );
        SwalAlert.error('Error de conexión', `${proc.name} no respondió. Verificá las credenciales y la URL.`);
      }
    }, 2000);
  };

  // --- Save config ---
  const handleSaveConfig = (proc: PaymentProcessor, config: Record<string, string>, instructions: string, mode: boolean) => {
    setProcessors((prev) =>
      prev.map((p) => {
        if (p.id !== proc.id) return p;
        return {
          ...p,
          testMode: mode,
          lastSync: new Date().toISOString().slice(0, 16).replace('T', ' '),
          config: p.config.map((c) => ({ ...c, value: config[c.key] || '' })),
          manualInstructions: p.category === 'manual' ? instructions : p.manualInstructions,
          receiverName: p.category === 'manual' && config['receiver_name'] ? config['receiver_name'] : p.receiverName,
          receiverContact: p.category === 'manual' && config['receiver_contact'] ? config['receiver_contact'] : p.receiverContact,
        };
      }),
    );
  };

  // --- Save commission ---
  const handleSaveCommission = (processorId: string, percent: number, fixed: number) => {
    setProcessors((prev) =>
      prev.map((p) => (p.id === processorId ? { ...p, commissionPercent: percent, commissionFixed: fixed } : p)),
    );
  };

  // --- Create processor ---
  const handleCreateProcessor = (proc: PaymentProcessor) => {
    setProcessors((prev) => [proc, ...prev]);
  };
  const handleSaveUsage = (processorId: string, functions: PaymentProcessorUsageFunction[]) => {
    setProcessors((prev) =>
      prev.map((p) => (p.id === processorId ? { ...p, usageFunctions: functions } : p)),
    );
  };

  // --- Reorder ---
  const handleMoveUp = (proc: PaymentProcessor) => {
    setProcessors((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((p) => p.id === proc.id);
      if (idx <= 0) return prev;
      const newOrder = sorted[idx - 1].order;
      sorted[idx - 1] = { ...sorted[idx - 1], order: proc.order };
      sorted[idx] = { ...sorted[idx], order: newOrder };
      return [...sorted].sort((a, b) => a.order - b.order);
    });
  };

  const handleMoveDown = (proc: PaymentProcessor) => {
    setProcessors((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((p) => p.id === proc.id);
      if (idx < 0 || idx >= sorted.length - 1) return prev;
      const newOrder = sorted[idx + 1].order;
      sorted[idx + 1] = { ...sorted[idx + 1], order: proc.order };
      sorted[idx] = { ...sorted[idx], order: newOrder };
      return [...sorted].sort((a, b) => a.order - b.order);
    });
  };

  const activeCount = processors.filter((p) => p.enabled).length;
  const apiCount = processors.filter((p) => p.category === 'api').length;
  const manualCount = processors.filter((p) => p.category === 'manual').length;
  const sortedProcs = [...processors].sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Procesadores de Pago</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Procesadores de Pago</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready'
                    ? `${activeCount} de ${processors.length} activos · ${apiCount} API · ${manualCount} manuales`
                    : 'Cargando configuración...'}
                </p>
              </div>
              <div className="flex items-center gap-2 self-start sm:self-auto">
                <span className="px-2.5 py-1 rounded-full bg-accent-200 text-accent-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1">
                  <i className="ri-information-line text-[9px]"></i>Configuración global
                </span>
                <button
                  onClick={() => setCreateModalOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line text-sm"></i>Nuevo Procesador
                </button>
              </div>
            </div>

            {/* Summary banners */}
            {loadingState === 'ready' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                <div className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                    <i className="ri-plug-line text-emerald-500 text-sm"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-400 text-[10px] font-medium truncate">Activos</p>
                    <p className="text-foreground-800 text-sm font-bold">{activeCount}</p>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                    <i className="ri-link-unlink text-red-400 text-sm"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-400 text-[10px] font-medium truncate">Inactivos</p>
                    <p className="text-foreground-800 text-sm font-bold">{processors.length - activeCount}</p>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                    <i className="ri-flask-line text-amber-500 text-sm"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-400 text-[10px] font-medium truncate">Modo Test</p>
                    <p className="text-foreground-800 text-sm font-bold">{processors.filter((p) => p.testMode).length}</p>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                    <i className="ri-webhook-line text-sky-500 text-sm"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-400 text-[10px] font-medium truncate">Webhooks OK</p>
                    <p className="text-foreground-800 text-sm font-bold">{processors.filter((p) => p.webhookStatus === 'healthy').length}</p>
                  </div>
                </div>
                <div className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                    <i className="ri-star-line text-violet-400 text-sm"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-400 text-[10px] font-medium truncate">Predeterminado</p>
                    <p className="text-foreground-800 text-sm font-bold">{processors.find((p) => p.isDefault)?.name || '—'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading */}
            {loadingState === 'loading' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="animate-pulse p-4 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4 py-2">
                      <div className="w-8 h-8 rounded-lg bg-background-200"></div>
                      <div className="flex-1 space-y-1.5">
                        <div className="w-24 h-3 bg-background-200 rounded"></div>
                        <div className="w-16 h-2 bg-background-200 rounded"></div>
                      </div>
                      <div className="w-20 h-6 bg-background-200 rounded-full"></div>
                      <div className="w-16 h-3 bg-background-200 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-400 text-2xl"></i>
                </div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar datos</p>
                <p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm"></i>Reintentar
                </button>
              </div>
            )}

            {/* Annual License Config Card */}
            {loadingState === 'ready' && (
              <AnnualLicenseConfigCard
                config={licenseConfig}
                editable={true}
                onEdit={() => setLicenseEditorOpen(true)}
              />
            )}

            {/* Table */}
            {loadingState === 'ready' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead>
                      <tr className="border-b border-background-200 bg-background-100/50">
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider w-8">#</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Procesador</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Modo</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Comisión</th>
                        <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Última prueba</th>
                        <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {sortedProcs.map((proc, idx) => {
                        const commissionDisplay =
                          proc.commissionPercent > 0 || proc.commissionFixed > 0
                            ? `${proc.commissionPercent}% + $${proc.commissionFixed.toFixed(2)}`
                            : '—';
                        const isFirst = idx === 0;
                        const isLast = idx === sortedProcs.length - 1;
                        const msg = inlineMessages[proc.id];

                        return (
                          <>
                            <tr
                              key={proc.id}
                              className={`hover:bg-background-100/60 transition-colors ${!proc.enabled ? 'opacity-60' : ''}`}
                            >
                            {/* Order */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-0.5">
                                <button
                                  onClick={() => handleMoveUp(proc)}
                                  disabled={isFirst}
                                  className={`w-5 h-5 rounded flex items-center justify-center cursor-pointer transition-colors ${isFirst ? 'text-foreground-200 cursor-default' : 'text-foreground-400 hover:text-foreground-600 hover:bg-background-200'}`}
                                >
                                  <i className="ri-arrow-up-s-line text-[11px]"></i>
                                </button>
                                <button
                                  onClick={() => handleMoveDown(proc)}
                                  disabled={isLast}
                                  className={`w-5 h-5 rounded flex items-center justify-center cursor-pointer transition-colors ${isLast ? 'text-foreground-200 cursor-default' : 'text-foreground-400 hover:text-foreground-600 hover:bg-background-200'}`}
                                >
                                  <i className="ri-arrow-down-s-line text-[11px]"></i>
                                </button>
                              </div>
                            </td>

                            {/* Processor */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${proc.logoColor}`}>
                                  <i className={`${proc.icon} text-sm`}></i>
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-foreground-800 text-xs font-semibold truncate">{proc.name}</p>
                                    {proc.isDefault && (
                                      <span className="px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-600 text-[8px] font-bold whitespace-nowrap">
                                        Default
                                      </span>
                                    )}
                                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-medium whitespace-nowrap ${proc.category === 'api' ? 'bg-sky-50 text-sky-600' : 'bg-amber-50 text-amber-600'}`}>
                                      {proc.category === 'api' ? 'API' : 'Manual'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleToggleEnabled(proc)}
                                className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${proc.enabled ? 'bg-emerald-500' : 'bg-background-300'}`}
                              >
                                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${proc.enabled ? 'translate-x-4.5' : 'translate-x-0.5'}`}></span>
                              </button>
                            </td>

                            {/* Mode */}
                            <td className="px-4 py-3">
                              {proc.category === 'api' ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold ${proc.testMode ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                                  <i className={`${proc.testMode ? 'ri-flask-line' : 'ri-rocket-line'} text-[9px]`}></i>
                                  {proc.testMode ? 'Sandbox' : 'Producción'}
                                </span>
                              ) : (
                                <span className="text-foreground-300 text-[10px]">—</span>
                              )}
                            </td>

                            {/* Commission */}
                            <td className="px-4 py-3">
                              <button
                                onClick={() => setCommissionProc(proc)}
                                className="text-foreground-800 text-xs font-mono font-semibold hover:text-foreground-950 transition-colors cursor-pointer hover:underline decoration-dotted underline-offset-2"
                              >
                                {commissionDisplay}
                              </button>
                            </td>

                            {/* Last test */}
                            <td className="px-4 py-3">
                              <span className="text-foreground-500 text-[10px] whitespace-nowrap">
                                {proc.lastTest || '—'}
                              </span>
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1 justify-end">
                                <button
                                  onClick={() => setDetailProc(proc)}
                                  className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                                  title="Ver detalle"
                                >
                                  <i className="ri-eye-line text-foreground-500 text-xs"></i>
                                </button>
                                <button
                                  onClick={() => setConfigProc(proc)}
                                  className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                                  title="Configurar"
                                >
                                  <i className="ri-settings-3-line text-foreground-500 text-xs"></i>
                                </button>
                                <button
                                  onClick={() => handleTestConnection(proc)}
                                  className="w-7 h-7 rounded-lg bg-sky-50 flex items-center justify-center cursor-pointer hover:bg-sky-100 transition-colors"
                                  title="Probar conexión"
                                >
                                  <i className="ri-plug-line text-sky-500 text-xs"></i>
                                </button>
                                <button
                                  onClick={() => handleSetDefault(proc)}
                                  className={`w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${proc.isDefault ? 'bg-accent-100 text-accent-500' : 'bg-background-100 text-foreground-400 hover:bg-background-200'}`}
                                  title={proc.isDefault ? 'Es el predeterminado' : 'Marcar como predeterminado'}
                                >
                                  <i className={`${proc.isDefault ? 'ri-star-fill' : 'ri-star-line'} text-xs`}></i>
                                </button>
                                <button
                                  onClick={() => setUsageProc(proc)}
                                  className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                                  title="Dónde se usa"
                                >
                                  <i className="ri-links-line text-foreground-500 text-xs"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                          {msg && (
                            <tr key={`${proc.id}-msg`}>
                              <td colSpan={7} className="px-4 py-0">
                                <div className={`flex items-center gap-1.5 px-3 py-2 mb-0.5 rounded-lg text-[10px] font-medium ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                  <i className={`${msg.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-information-line text-amber-500'} text-xs`}></i>
                                  {msg.text}
                                </div>
                              </td>
                            </tr>
                          )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Empty state */}
                {sortedProcs.length === 0 && (
                  <div className="p-12 text-center">
                    <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                      <i className="ri-bank-card-line text-foreground-300 text-xl"></i>
                    </div>
                    <p className="text-foreground-500 text-sm font-semibold">Sin procesadores</p>
                    <p className="text-foreground-400 text-xs mt-1">No hay procesadores de pago configurados.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      <ProcessorDetailModal open={detailProc !== null} onClose={() => setDetailProc(null)} processor={detailProc} />
      <ProcessorConfigModal open={configProc !== null} onClose={() => setConfigProc(null)} processor={configProc} onSave={handleSaveConfig} />
      <ProcessorCommissionModal open={commissionProc !== null} onClose={() => setCommissionProc(null)} processor={commissionProc} onSave={handleSaveCommission} />
      <ProcessorUsageModal open={usageProc !== null} onClose={() => setUsageProc(null)} processor={usageProc} onSave={handleSaveUsage} />
      <AnnualLicenseEditorModal open={licenseEditorOpen} onClose={() => setLicenseEditorOpen(false)} config={licenseConfig} onSave={setLicenseConfig} />
      <CreateProcessorModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onCreate={handleCreateProcessor} />

      {/* Mobile footer */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Procesadores de Pago</p>
      </div>
    </div>
  );
}